import { ReactNode } from 'react';

import { AppIcon, AppIconProps } from '@backrs_ui/common-ui';

import { Box, Button, Typography, SxProps, Theme, Skeleton } from '@mui/material';

export const ASPIRATION_CARD_DATA_ATTR = 'data-aspiration-card';

type AspirationCardProps = Readonly<{
  icon: ReactNode;
  title: string;
  description?: string;
}>;

export function AspirationCard({ icon, title, description }: AspirationCardProps) {
  return (
    <Box {...{ [ASPIRATION_CARD_DATA_ATTR]: 'true' }} sx={cardSx}>
      <Box sx={badgeSx}>
        <Box sx={iconSx}>{icon}</Box>
        <Typography variant="text-sm-medium" sx={badgeTextSx}>
          {title}
        </Typography>
      </Box>
      {description ? (
        <Typography
          variant="text-xs-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {description}
        </Typography>
      ) : (
        <Box sx={skeletonSx}>
          <Skeleton variant="text" width="100%" height={18} />
          <Skeleton variant="text" width="75%" height={18} />
        </Box>
      )}
    </Box>
  );
}

type AspirationActionCardProps = Readonly<{
  buttonId: string;
  prompt: string;
  buttonLabel: string;
  buttonIconName: AppIconProps['iconName'];
  onClick: () => void;
}>;

export function AspirationActionCard({
  buttonId,
  prompt,
  buttonLabel,
  buttonIconName,
  onClick,
}: AspirationActionCardProps) {
  return (
    <Box sx={actionCardSx}>
      <Typography
        variant="text-xs-regular"
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
      >
        {prompt}
      </Typography>
      <Button
        id={buttonId}
        variant="contained"
        size="md"
        hierarchy="Secondary color"
        sx={actionButtonSx}
        onClick={onClick}
      >
        <AppIcon iconName={buttonIconName} diameter="20px" color="currentColor" />
        {buttonLabel}
      </Button>
    </Box>
  );
}

const cardStyle = ({ backrs }: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: backrs.spacing['spacing-lg'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingX: backrs.spacing['spacing-2xl'],
  paddingY: backrs.spacing['spacing-xl'],
});

const cardSx: SxProps<Theme> = cardStyle;

const badgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  borderRadius: backrs.radius['radius-sm'],
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
  width: 'fit-content',
});

const iconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  display: 'flex',
  alignItems: 'center',
});

const badgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
});

const skeletonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const actionCardSx: SxProps<Theme> = (theme) => ({
  ...cardStyle(theme),
  justifyContent: 'center',
});

const actionButtonSx: SxProps<Theme> = ({ backrs }) => ({
  width: 'fit-content',
  gap: backrs.spacing['spacing-xs'],
});
